import { useEffect, useState } from 'react';
import { Building2, Check, ChevronDown, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { getActiveStudioId, onActiveStudioChange, ownerListStudios, setActiveStudioId } from '@/lib/api';
import { ADMIN_TENANT } from '@/constants/testIds';

const statusPill = {
  approved: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200',
  pending: 'border-[#C9A45C]/25 bg-[#C9A45C]/10 text-[#D7B46A]',
  suspended: 'border-red-500/25 bg-red-500/10 text-red-200',
};

/**
 * LXRA owner only: pick which tenant the dashboard shows. "All studios" is the
 * unscoped overview; picking a studio sends X-Studio-Id on every request.
 */
export const TenantSwitcher = ({ onChange }) => {
  const [studios, setStudios] = useState([]);
  const [active, setActive] = useState(getActiveStudioId());

  useEffect(() => {
    ownerListStudios().then((d) => setStudios(d.studios)).catch(() => setStudios([]));
    return onActiveStudioChange(setActive); // stay in sync with "view as" from other tabs
  }, []);

  const pick = (id) => {
    setActiveStudioId(id);
    setActive(id);
    onChange?.(id);
  };

  const current = studios.find((s) => s.id === active);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={ADMIN_TENANT.switcherTrigger}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] text-xs text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))]/60 transition-colors"
        >
          {active ? <Eye className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> : <Building2 className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" />}
          <span className="max-w-[180px] truncate">{active ? `Viewing as ${current?.name || '…'}` : 'All studios'}</span>
          {current && <span className={`rounded-full border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.08em] ${statusPill[current.status] || ''}`}>{current.status}</span>}
          <ChevronDown className="h-3.5 w-3.5 text-[hsl(var(--lxra-muted))]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={ADMIN_TENANT.switcherMenu} className="w-72 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">Tenant</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => pick(null)} data-testid={ADMIN_TENANT.switcherAll} className="text-xs focus:bg-[hsl(var(--lxra-gold))]/15 focus:text-[hsl(var(--lxra-gold))]">
          <Building2 className="h-3.5 w-3.5 mr-2" /> All studios (overview)
          {!active && <Check className="h-3.5 w-3.5 ml-auto" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[hsl(var(--lxra-border))]" />
        {studios.map((s) => (
          <DropdownMenuItem key={s.id} onClick={() => pick(s.id)} data-testid={ADMIN_TENANT.switcherItem(s.id)} className="text-xs focus:bg-[hsl(var(--lxra-gold))]/15 focus:text-[hsl(var(--lxra-gold))]">
            <span className="truncate">{s.name}</span>
            <span className={`ml-2 rounded-full border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.08em] ${statusPill[s.status] || ''}`}>{s.status}</span>
            {active === s.id && <Check className="h-3.5 w-3.5 ml-auto" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
